/**
 * The grouped navigation list, as BEETS lays its sidebar out.
 *
 * Marked up as nav > ul > li > a so that every item is a real link: it can be
 * middle-clicked into a new tab, read out by a screen reader as a link, and
 * found by tests the same way a person finds it.
 */

import { Link as RouterLink, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Tooltip from '@mui/material/Tooltip'
import { buildNavigation, currentPath } from '../../navigation'
import { useSchema } from '../../state/AppData'

export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const schema = useSchema()
  const { pathname } = useLocation()
  const groups = buildNavigation(schema)
  // Decided once across every group rather than per item: '/verification' is a
  // prefix of '/verification/flowdown', so asking each item in isolation
  // highlights two at once.
  const current = currentPath(groups, pathname)

  return (
    <Box component="nav" aria-label="Main navigation">
      {groups.map((group, index) => (
        <Box key={group.label}>
          {index > 0 && <Divider sx={{ my: 0.5 }} />}
          <List
            dense
            subheader={
              <ListSubheader
                disableSticky
                sx={{ bgcolor: 'transparent', lineHeight: '2.2rem', fontWeight: 600 }}
              >
                {group.label}
              </ListSubheader>
            }
          >
            {group.items.map((item) => (
              <ListItem key={item.path} disablePadding>
                {/* describeChild: without it the tooltip becomes the link's
                    aria-label, so a screen reader announces the explanation
                    instead of the destination. The label is the name; the
                    tooltip only describes it. */}
                <Tooltip
                  title={item.tooltip}
                  describeChild
                  placement="right"
                  enterDelay={600}
                >
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    // ButtonBase stamps role="button" on anything that is not
                    // a native <button>, which overrides the anchor's own
                    // role. These navigate, so they are links: a screen reader
                    // should say so, and "open in new tab" should make sense.
                    role="link"
                    selected={item.path === current}
                    onClick={onNavigate}
                    sx={{ borderRadius: 1, mx: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  )
}
